
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	Avatar,
	IMAGE_SERVER_NORMALIZATION_EXTENSIONS,
	IMAGE_STANDARD_WEB_IMAGE_EXTENSIONS,
	RunnitUpload,
	AVATAR_USE_CASE,
	IMAGE_UPLOAD_MODE,
	NsfwThreshold,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { ImageUploadMixin } from '@/mixins/ImageUploadMixin';
import { buildScreeningProxy, fileFromImageBlob, requiresServerNormalization } from '@/utils/imageUtils';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import { uploadImageRecordForUseCase } from '@/utils/runnitUpload';
import { UploadImageError, getUploadImageErrorMessage, preflightUploadSize } from '@/utils/uploadErrors';
import {
	acceptsAsset3DDrop,
	isAsset3DDraggedItem,
} from '@/views/Runnits/RunnitSettings/Asset3D/asset3DDrop';
import { fetchAsset3DAsFile } from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFile';
import { functionsBaseUrl } from '@/firebase';

export default Vue.extend({
	name: 'ImageUpload',
	props: {
		mode: {
			type: String,
			required: true,
			validator: (val: string) => Object.keys(IMAGE_UPLOAD_MODE).includes(val),
		},
		avatarUseCase: {
			type: String,
			default: AVATAR_USE_CASE.RUNNIT,
			validator: (val: string) => Object.keys(AVATAR_USE_CASE).includes(val),
		},
		multiple: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		allowTextFiles: { type: Boolean, default: false },
		dropZone: { type: Boolean, default: true },
		// When true (and mode is RUNNIT_UPLOAD), the file picker also accepts .glb/.gltf/.obj/.stl;
		// picking one opens Pose3DEditor instead of going through the image upload pipeline.
		hasPose3D: { type: Boolean, default: false },
		// Required when hasPose3D is true so the editor state can be keyed by field.uuid.
		fieldUuid: { type: String, default: null },
		// Set by multi-image fields at their image limit. Without it a drop starts
		// the whole Pose 3D flow and handleImageUpload then truncates the capture
		// away, so the user pays for a modal interaction that silently discards
		// its result.
		atCapacity: { type: Boolean, default: false },
		atCapacityMessage: { type: String, default: 'Maximum number of images reached' },
	},
	mixins: [
		ImageUploadMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	data () {
		return {
			isUploading: false,
			isBeingDraggedOver: false,
			checkingNsfw: false,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'dragAndDrop',
			'team',
		]),
		...mapGetters([
			'companyClubOfferBenefits',
		]),
		acceptedFileTypes () {
			const base = this.allowTextFiles
				? ['image/jpeg', 'image/png', 'image/webp', 'image/bmp', 'image/heic', 'image/heif', 'image/tiff', 'text/plain']
				: ['image/jpeg', 'image/png', 'image/webp', 'image/bmp', 'image/heic', 'image/heif', 'image/tiff'];
			if (this.hasPose3D && this.mode === IMAGE_UPLOAD_MODE.RUNNIT_UPLOAD) {
				return [...base, '.glb', '.gltf', '.obj', '.stl'];
			}
			return base;
		},
		isDisabled () {
			const permissionDisabled = this.mode === IMAGE_UPLOAD_MODE.RUNNIT_UPLOAD && !this.hasGenerateRunnitPermission;
			return this.disabled || permissionDisabled;
		},
		accepts3D () {
			return this.hasPose3D && this.mode === IMAGE_UPLOAD_MODE.RUNNIT_UPLOAD;
		},
		isDraggingActive () {
			return this.dragAndDrop.isDragging && !this.isDisabled;
		},
		hasActiveDropZones () {
			return this.dragAndDrop.activeDropZones > 0;
		}
	},
	watch: {
		'dragAndDrop.isDragging': {
			immediate: true,
			handler (isDragging) {
				// Only set allowDrop if this component is a drop zone
				if (this.dropZone) {
					this.setAllowDrop(!this.isDisabled && isDragging);
				}
			}
		},
		'dragAndDrop.activeDropZones': {
			immediate: true,
			handler (activeDropZones) {
				// If we're in a dragging state but there are no active drop zones, restore this one
				// Only do this if this component is a drop zone
				if (this.dropZone && this.dragAndDrop.isDragging && activeDropZones === 0) {
					this.registerAsDropZone();
				}
			}
		},
		// Watch for changes to the dropZone prop
		dropZone: {
			immediate: false, // Don't run on component creation (handled in mounted)
			handler (newValue, oldValue) {
				if (newValue && !oldValue) {
					// If changing from not a drop zone to a drop zone, register
					this.registerAsDropZone();
				} else if (!newValue && oldValue) {
					// If changing from a drop zone to not a drop zone, unregister
					this.unregisterAsDropZone();
				}
			}
		}
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
			'setAllowDrop',
			'setIsDragging',
			'setDraggedItem',
			'addDropZone',
			'removeDropZone',
			'updatePose3DEditorState',
		]),
		is3DFile (file: File): boolean {
			const ext = (file.name.split('.').pop() || '').toLowerCase();
			return ['glb', 'gltf', 'obj', 'stl'].includes(ext);
		},
		openPose3DEditorWithFile (file: File) {
			if (!this.fieldUuid) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: '3D upload is not configured for this field',
					show: true,
				});
				return;
			}
			this.updatePose3DEditorState({
				fieldUuid: this.fieldUuid,
				editorState: {
					open: true,
					saving: false,
					assetFile: file,
					assetName: file.name,
				},
			});
		},
		async handleAsset3DDrop (draggedItem) {
			if (this.atCapacity) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: this.atCapacityMessage,
					show: true,
				});
				this.setIsDragging(false);
				this.setDraggedItem(null);
				return;
			}
			try {
				this.$emit('on-upload-start');
				this.isUploading = true;
				const file: File = await fetchAsset3DAsFile({
					nodeRunResult: draggedItem.nodeRunResult || null,
					upload: draggedItem.upload || null,
				});
				this.openPose3DEditorWithFile(file);
			} catch (e: any) {
				console.error('Error opening dragged 3D asset:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e && e.message ? e.message : 'Could not open that 3D asset',
					show: true,
				});
			} finally {
				this.isUploading = false;
				this.$emit('on-upload-end');
				this.setIsDragging(false);
				this.setDraggedItem(null);
				this.clearAllSelectionStates();
			}
		},
		clearAllSelectionStates () {
			const activeSelectionStates = this.orderedSelectionStates;

			// Exit selection mode for all active selection states
			activeSelectionStates.forEach(state => {
				if (state.triggeringId) {
					this.exitSelectionMode(state.triggeringId);
				}
			});
		},
		onUploadComplete (val: RunnitUpload[]) {
			const filesToEmit = this.multiple ? val : val[0];
			this.$emit('on-upload-complete', filesToEmit);
		},
		handleDragOver (ev) {
			if (this.isDisabled || !this.dropZone) return;

			// A 3D payload is only ever welcome on a field that can open the editor;
			// everywhere else it must keep falling through to the image checks below
			// and be rejected exactly as it was before.
			if (this.accepts3D && !this.atCapacity && acceptsAsset3DDrop(
				true,
				this.dragAndDrop.isDragging ? this.dragAndDrop.draggedItem : null,
				ev.dataTransfer.items,
			)) {
				this.isBeingDraggedOver = true;
				ev.dataTransfer.dropEffect = 'copy';
				ev.preventDefault();
				return;
			}

			// Check if it's a RunnitImage being dragged
			if (this.dragAndDrop.isDragging && this.dragAndDrop.draggedItem && this.dragAndDrop.draggedItem.type === 'RUNNIT_IMAGE') {
				// An ASSET_3D dragged onto a field without 3D support is not an image.
				if (isAsset3DDraggedItem(this.dragAndDrop.draggedItem)) {
					ev.preventDefault();
					return;
				}
				this.isBeingDraggedOver = true;
				ev.dataTransfer.dropEffect = 'copy';
			} else if (ev.dataTransfer.items && ev.dataTransfer.items.length > 0) {
				// Check if it's a file being dragged
				let hasValidFile = false;
				for (let i = 0; i < ev.dataTransfer.items.length; i++) {
					const item = ev.dataTransfer.items[i];
					if (item.kind === 'file' && (item.type.startsWith('image/') || (this.allowTextFiles && item.type === 'text/plain'))) {
						hasValidFile = true;
						break;
					}
				}

				if (hasValidFile) {
					this.isBeingDraggedOver = true;
					ev.dataTransfer.dropEffect = 'copy';
				}
			}

			ev.preventDefault();
		},
		handleDragLeave (ev) {
			if (this.isUploading || this.isDisabled || !this.dropZone || this.checkingNsfw) return;
			this.isBeingDraggedOver = false;
			ev.preventDefault();
		},
		async handleDrop (ev) {
			if (this.isDisabled || !this.dropZone) return;
			this.isBeingDraggedOver = false;

			// A dragged 3D asset routes to the editor instead of the image pipeline.
			// This has to run before the RUNNIT_IMAGE branch, which would otherwise
			// commit the model as though it were an image.
			if (this.accepts3D && this.dragAndDrop.isDragging && isAsset3DDraggedItem(this.dragAndDrop.draggedItem)) {
				await this.handleAsset3DDrop(this.dragAndDrop.draggedItem);
				return;
			}

			// Check if it's a RunnitImage being dropped
			if (this.dragAndDrop.isDragging && this.dragAndDrop.draggedItem && this.dragAndDrop.draggedItem.type === 'RUNNIT_IMAGE') {
				// Without 3D support this is not a droppable image; drop it silently
				// rather than uploading a model into an image field.
				if (isAsset3DDraggedItem(this.dragAndDrop.draggedItem)) {
					this.setIsDragging(false);
					this.setDraggedItem(null);
					return;
				}
				// If we're in avatar mode, don't allow dropping already uploaded files
				if (this.mode === 'AVATAR') {
					console.log('Dropping images not supported in avatar mode');
					this.updateSnackbar({
						status: SNACKBAR_STATUS.WARN,
						message: `Dropping images is not supported in avatar mode`,
						show: true,
					});

					// Reset drag state
					this.setIsDragging(false);
					this.setDraggedItem(null);
					return;
				}

				const draggedItem = this.dragAndDrop.draggedItem;

				try {
					this.$emit('on-upload-start');
					this.isUploading = true;

					if (draggedItem.nodeRunResult) {
						this.onUploadComplete([{ nodeRunResult: draggedItem.nodeRunResult }]);
					} else if (draggedItem.upload) {
						this.onUploadComplete([{ upload: draggedItem.upload }]);
					} else if (draggedItem.src) {
						// Handle case where we only have the image source URL
						try {
							const response = await fetch(draggedItem.src);
							const blob = await response.blob();
							// Name and type come from the response, never assumed. Calling
							// every dragged blob image/png stored JPEG bytes as PNG and
							// sent a dragged HEIC down the verbatim path, where nothing
							// converts it.
							const file = fileFromImageBlob(blob, draggedItem.src);
							const upload = await this.uploadFile(file);
							// Both branches below fall through to the drag-state resets
							// rather than returning. An early return here exits
							// handleDrop entirely, skipping setIsDragging(false) and
							// setDraggedItem(null), which leaves every drop zone in the
							// app stuck in its dragged-over state for the rest of the
							// session.
							if (upload) {
								this.onUploadComplete([{ upload }]);
							}
						} catch (e) {
							console.error('Error processing image from src:', e);
							this.updateSnackbar({
								status: SNACKBAR_STATUS.ERROR,
								message: getUploadImageErrorMessage(e),
								show: true,
							});
						}
					} else {
						console.error('Dropped item has no recognizable content:', draggedItem);
					}

					// Explicitly reset the drag state after processing
					this.setIsDragging(false);
					this.setDraggedItem(null);

					// Clear all active selection states
					this.clearAllSelectionStates();
				} catch (e) {
					console.error('Error processing dragged image:', e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Error processing dragged image`,
						show: true,
					});

					// Reset drag state even if there's an error
					this.setIsDragging(false);
					this.setDraggedItem(null);
				} finally {
					this.isUploading = false;
					this.$emit('on-upload-end');
				}

				// Ensure drag state is reset
				this.setIsDragging(false);
				this.setDraggedItem(null);

				// Clear all active selection states
				this.clearAllSelectionStates();
				return;
			}

			// Handle regular file drop
			let dt = ev.dataTransfer;
			let files = dt.files;
			await this.handleFiles(files);
		},
		async onHiddenFileInputChange (ev) {
			if (this.isUploading || this.checkingNsfw) return;
			this.isBeingDraggedOver = false;
			await this.handleFiles(ev.target.files);

			// Clear the selected file from the input
			this.$refs.fileInput.value = ''; // Reset the value of the input element
		},
		openFileDialog () {
			if (this.isUploading || this.isDisabled || this.checkingNsfw) return;
			this.$refs.fileInput.click();
		},
		async handleFiles (fileList: FileList) {
			this.isBeingDraggedOver = false;
			const files = Array.from(fileList);
			if (!files || !files.length) return;

			if (this.hasPose3D && this.mode === IMAGE_UPLOAD_MODE.RUNNIT_UPLOAD) {
				const first3DFile = files.find(f => this.is3DFile(f));
				if (first3DFile) {
					if (this.atCapacity) {
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: this.atCapacityMessage,
							show: true,
						});
						return;
					}
					this.openPose3DEditorWithFile(first3DFile);
					return;
				}
			}

			// Filter by accepted file types
			const isFileAccepted = (file: File): boolean => {
				if (this.acceptedFileTypes.includes(file.type)) return true;
				const extension: string = file.name.split('.').pop()?.toLowerCase() || '';
				return (
					[...IMAGE_SERVER_NORMALIZATION_EXTENSIONS, ...IMAGE_STANDARD_WEB_IMAGE_EXTENSIONS].includes(extension)
				);
			};
			const validFiles = files.filter(file => isFileAccepted(file));
			const invalidFiles = files.filter(file => !isFileAccepted(file));
			if (invalidFiles.length) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Invalid file format. Please upload ${this.acceptedFileTypes.join(', ')} files only`,
					show: true,
				});
			}
			if (!validFiles.length) return;

			try {
				this.$emit('on-upload-start');
				this.isUploading = true;
				// Uploaded byte for byte. Nothing here rewrites the user's file.
				// Size-check the batch before starting any of it. uploadFile gates
				// each file too, but inside Promise.all a rejection abandons its
				// siblings rather than cancelling them: they run on to completion in
				// the background and write records the UI has already discarded.
				validFiles.forEach((file: File) => preflightUploadSize(file));

				const uploads = await Promise.all(validFiles.map(file => this.uploadFile(file)));
				const filteredUploads = uploads.filter(upload => !!upload);
				if (!filteredUploads.length) {
					return;
				}

				this.onUploadComplete(uploads);

				// Clear all active selection states
				this.clearAllSelectionStates();
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: getUploadImageErrorMessage(e),
					show: true,
				});

				// Clear all active selection states
				this.clearAllSelectionStates();
			} finally {
				this.isUploading = false;
				this.$emit('on-upload-end');
			}
		},
		async isNsfw (file) {
			this.checkingNsfw = true;
			const nsfwThreshold: NsfwThreshold = (this.companyClubOfferBenefits.isEnterprise && this.team?.nsfwThreshold) || 'DEFAULT';

			// Screen a small proxy, not the file: the classifier takes base64 inline
			// and a large upload would exceed Cloud Run's request cap, which the
			// catch below would swallow into a silent pass. The original uploads
			// untouched.
			let screened: Blob;
			try {
				screened = await buildScreeningProxy(file);
			} catch (err) {
				if (requiresServerNormalization(file)) {
					// HEIC, HEIF, TIFF and BMP cannot be decoded in a browser at all,
					// so no proxy has ever been possible for them. Screen the source,
					// exactly as before. These are bounded by the 28MB POST ceiling
					// either way.
					console.warn('Screening proxy unavailable for a server-normalized format; screening the source file', err);
					screened = file;
				} else {
					// A displayable image we could not decode is the case this task
					// made dangerous: it can now be 100MB, and posting it would 413
					// and pass silently. Refuse instead, with a kind the message
					// helper recognises.
					this.checkingNsfw = false;
					throw new UploadImageError(
						'SCREENING_UNAVAILABLE',
						'Screening proxy could not be built for a browser-displayable image',
						{ cause: err },
					);
				}
			}

			const reader: FileReader = new FileReader();

			return new Promise((resolve, reject) => {
				reader.onload = async () => {
					const result = reader.result;
					if (typeof result === 'string') {
						const base64Image = result.split(',')[1]; // Get base64 part
						try {
							const response = await fetch(`${functionsBaseUrl}/isNsfw`, {
								method: 'POST',
								headers: {
									'Content-Type': 'application/json',
								},
								body: JSON.stringify({
									base64Images: [base64Image],
									nsfwThreshold,
								}),
							});

							const data = await response.json();

							// Check if we have results and at least one result
							if (data.results && data.results.length > 0) {
								if (data.results[0].isNsfw) {
									console.log('nsfw probability', data.results[0].probability);
								}
								this.checkingNsfw = false;
								resolve(data.results[0].isNsfw);
							} else {
								this.checkingNsfw = false;
								resolve(false); // Default to false if no clear result
							}
						} catch (error) {
							console.error('Error calling isNsfw:', error);
							this.checkingNsfw = false;
							resolve(false); // Default to false on error
						}
					} else {
						this.checkingNsfw = false;
						reject(new Error('Failed to read file as a string'));
					}
				};
				reader.onerror = reject;
				reader.readAsDataURL(screened);
			});
		},
		async uploadFile (file) {
			// Size first, before anything decodes the file: isNsfw below builds a
			// screening proxy, which is a full decode. This is the only gate in the
			// component because every route into an upload goes through here,
			// including the URL drag above, which does not pass through handleDrop.
			preflightUploadSize(file);
			const nsfwResults = await this.isNsfw(file);
			if (nsfwResults) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error uploading image: triggered NSFW filter`,
					show: true,
				});
				return null;
			}
			if (this.mode === 'RUNNIT_UPLOAD') {
				return this.handleRunnitUpload(file);
			} else if (this.mode === 'AVATAR') {
				return this.handleAvatar(file);
			}
		},
		async handleRunnitUpload (file: File) {
			return await uploadImageRecordForUseCase<RunnitUpload>({
				file,
				uploadType: 'RUNNIT_UPLOAD',
			});
		},
		async handleAvatar (file: File) {
			// NOTE: the firebase size extension will be making this 256x256 and then deleting the originals
			return await uploadImageRecordForUseCase<Avatar>({
				file,
				uploadType: 'AVATAR',
				useCase: this.avatarUseCase || AVATAR_USE_CASE.RUNNIT,
			});
		},
		handleDocumentDrop (ev) {
			// Only handle the document drop if we're in a dragging state, this component is still active,
			// and this component is a drop zone
			if (this.dropZone && this.dragAndDrop.isDragging && this.hasActiveDropZones) {

				// Don't process drops in avatar mode
				if (this.mode === 'AVATAR' && this.dragAndDrop.draggedItem && this.dragAndDrop.draggedItem.type === 'RUNNIT_IMAGE') {

					// Reset drag state
					this.setIsDragging(false);
					this.setDraggedItem(null);
					return;
				}

				// Prevent default browser behavior
				ev.preventDefault();

				// Process the drop using our component's handler
				this.handleDrop(ev);
			}
		},
		handleGlobalDragEnd () {
			// This ensures the drag state is reset even if the component unmounts during drag
			// Only affect the global state if this component is a drop zone
			if (this.dropZone) {
				this.cleanupDragState();
			}
		},
		cleanupDragState () {
			// Only cleanup if this component is a drop zone
			if (this.dropZone) {
				// Reset the drag state
				this.setIsDragging(false);
				this.setDraggedItem(null);

				// Clear all active selection states
				this.clearAllSelectionStates();
			}
		},
		// Method to register this component as a drop zone
		registerAsDropZone () {
			if (this.dropZone) {
				this.addDropZone();
			}
		},
		// Method to unregister this component as a drop zone
		unregisterAsDropZone () {
			if (this.dropZone) {
				this.removeDropZone();
			}
		},
	},
	components: {
	},
	created () {
		// If we're in the middle of a drag operation and this is a drop zone, register immediately
		if (this.dropZone && this.dragAndDrop && this.dragAndDrop.isDragging) {
			this.registerAsDropZone();
		}
	},
	mounted () {
		// Only register as a drop zone if the dropZone prop is true
		if (this.dropZone) {
			this.registerAsDropZone();
		}

		// Add event listener for dragend to ensure we clean up if the component unmounts during drag
		window.addEventListener('dragend', this.handleGlobalDragEnd);

		// Add document-level drop handler as a fallback
		document.addEventListener('drop', this.handleDocumentDrop);

		// Check if we're in the middle of a drag operation and restore state if needed
		if (this.dropZone && this.dragAndDrop.isDragging && !this.hasActiveDropZones) {
			this.registerAsDropZone();
		}
	},
	beforeDestroy () {
		// Only unregister as a drop zone if the dropZone prop is true
		if (this.dropZone) {
			// Only unregister as a drop zone if we're not in the middle of a drag operation
			if (!this.dragAndDrop.isDragging) {
				this.unregisterAsDropZone();
			}
		}

		// Remove the global event listeners
		window.removeEventListener('dragend', this.handleGlobalDragEnd);
		document.removeEventListener('drop', this.handleDocumentDrop);
	},
});
